import { toast } from "react-toastify";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  ButtonColorOption,
  ButtonVariantOption,
  LinkButton,
  SubmitButton,
} from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { KeyboardArrowLeft } from "@mui/icons-material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import React, { useEffect, useRef, useState } from "react";
import { useFeatureFlags } from "@/configs/featureFlag";
import { DateRangePicker } from "@/components/DatePicker";
import {
  getSearchMaxDate,
  getSearchMinDate,
} from "@/features/realEstateReceptionBookFeed/utils/dateTime";
import { MonitoringListMultiSelect } from "../components/MonitoringListMultiSelect";
import { useReceptionReasonOptions } from "@/features/realEstateReceptionBookFeed/hooks/useReceptionReasonOptions";
import { MonitoringFeedGrid } from "@/features/monitoring/components/MonitoringFeedGrid";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionDetails from "@mui/material/AccordionDetails";
import { useMonitoringNotifications } from "@/features/monitoring/hooks/useMonitoringNotifications";
import { GetMonitoringAsFileAPI } from "@/features/monitoring/api/GetMonitoringAsFileAPI";
import { useApiClient } from "@/hooks/useApiClient";
import { type AxiosResponse, HttpStatusCode, isAxiosError } from "axios";
import { CustomModal } from "@/components/Modal";
import {
  type UpdateParameterEntity,
  type GetMonitoringSummariesAPIResponse,
  type IMonitoringNotificationsObject,
} from "../types";
import { PutMonitoringAPI } from "@/features/monitoring/api/PutMonitoringAPI";
import { SendToTrackingService } from "@/api/handlers/errors";
import { MonitoringResultModal } from "./MonitoringResultModal";
import { useReceptionBookImportStatuses } from "@/features/realEstateReceptionBookFeed/hooks/useReceptionBookImportStatuses";
import { ReceptionBookImportStatus } from "@/features/realEstateReceptionBookFeed/components/ReceptionBookImportStatus";
import { Label } from "@/components/Label";
export const Monitoring: React.FC = () => {
  const {
    mypageTop,
    realEstateRegistrationMonitoring,
    realEstateRegistrationMonitoringInformation,
  } = useFeatureFlags();

  // FeedGridに更新を指示するためのref
  const ref = useRef<{ reload: () => void }>(null);

  const startDate = new Date();
  const minDate = getSearchMinDate();
  const maxDate = getSearchMaxDate();

  const { receptionReasonOptions } = useReceptionReasonOptions({ short: true });

  const [createdDateStart, setCreatedDateStart] = useState<Date | null>(null);
  const [createdDateEnd, setCreatedDateEnd] = useState<Date | null>(null);

  const [receptionReasons, setReceptionReasons] = useState<string[]>([]);
  const [apiResponseData, setApiResponseData] = useState<
    GetMonitoringSummariesAPIResponse | undefined
  >(undefined);
  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>([]);

  const [disableFileDownloadButton, setDisableFileDownloadButton] =
    useState<boolean>(false);

  const [
    disableBulkMonitoringCancelButton,
    setDisableBulkMonitoringCancelButton,
  ] = useState<boolean>(false);

  // モーダル表示用State
  const [isOpen, setIsOpen] = useState<boolean>(false);
  // 確認ボタン押下後のキャンセルボタン無効化State
  const [isPushedConfirmButton, setIsPushedConfirmButton] =
    useState<boolean>(false);

  const importStatuses = useReceptionBookImportStatuses();

  // モニタリング通知
  const { data: notifications, isLoading } = useMonitoringNotifications();

  // モニタリング結果ダイアログ表示用State
  const [openResultDialog, setOpenResultDialog] = useState<boolean>(false);

  // モニタリング結果ダイアログ用のデータ
  const [monitoringIdForResultDialog, setMonitoringIdForResultDialog] =
    useState<number | null>(null); // モニタリングID

  const { apiClient } = useApiClient();

  // 一括解除ボタン押下時の処理
  const handleSelectedRowCancel = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    setIsPushedConfirmButton(true);
    if (selectedRowIds.length !== 0 && apiResponseData) {
      const updateParameters = apiResponseData.summaries
        .filter((summary): boolean => {
          return (
            selectedRowIds.includes(summary.id) &&
            (summary?.monthlyUpdatable ?? false)
          );
        })
        .map((summary): UpdateParameterEntity => {
          return {
            id: summary.id,
            name: summary.name,
            receptionReasonIds: summary.receptionReasons.map((reason) =>
              String(reason.id)
            ),
            realEstateOwnerAnalyzeRequestId:
              summary.realEstateOwnerAnalyzeRequestId,
            pictureRequestId: summary.pictureRequestId,
            monthlyUpdatable: false,
            legalAffairsBureauRequestDateStart:
              summary.legalAffairsBureauRequestDateStart,
            legalAffairsBureauRequestDateEnd:
              summary.legalAffairsBureauRequestDateEnd,
          };
        });

      PutMonitoringAPI(apiClient, { updateParameters })
        .then(() => {
          toast.success("モニタリング登録を解除しました。");
        })
        .catch((error) => {
          if (
            isAxiosError<AxiosResponse>(error) &&
            (error.response?.status === HttpStatusCode.BadRequest ||
              error.response?.status === HttpStatusCode.NotFound)
          ) {
            toast.error("指定のデータが存在しません。");
            return;
          }
          if (
            isAxiosError<AxiosResponse>(error) &&
            (error.response?.status ?? HttpStatusCode.InternalServerError) >=
              HttpStatusCode.InternalServerError
          ) {
            toast.error(
              "モニタリング登録の解除に失敗しました。しばらく経ってから再度お試しください。"
            );
          }
        })
        .finally(() => {
          setSelectedRowIds([]);
          setIsOpen(false);
          setIsPushedConfirmButton(false);
          cancelLoading();
          ref.current?.reload();
        });
    }
  };

  // 一括ダウンロード処理
  const handleCsvDownloadButtonClick = (): void => {
    if (selectedRowIds.length !== 0) {
      GetMonitoringAsFileAPI(apiClient, { ids: selectedRowIds }).catch(
        (error) => {
          if (
            isAxiosError<AxiosResponse>(error) &&
            error.response?.status === HttpStatusCode.BadRequest
          ) {
            toast.error("CSVファイルのダウンロードに失敗しました。");
            return;
          }
          if (
            isAxiosError<AxiosResponse>(error) &&
            (error.response?.status ?? HttpStatusCode.InternalServerError) >=
              HttpStatusCode.InternalServerError
          ) {
            SendToTrackingService(error);
            toast.error("CSVファイルのダウンロードに失敗しました");
          }
        }
      );
    }
  };

  const getFormattedDate = (date: Date | null): string | null => {
    // NOTE: Invalid Dateの場合があり得るのでnullを返す
    return date && !Number.isNaN(date.getTime())
      ? format(date, "yyyy-MM-dd")
      : null;
  };

  // 通知ラベルのクリック処理
  const handleNotificationLabelClick = (
    notification: IMonitoringNotificationsObject
  ): void => {
    setMonitoringIdForResultDialog(notification.id);
    setOpenResultDialog(true);
  };

  useEffect(() => {
    if (selectedRowIds.length === 0) {
      setDisableFileDownloadButton(true);
      setDisableBulkMonitoringCancelButton(true);
      return;
    }

    if (apiResponseData == null) {
      setDisableFileDownloadButton(true);
      setDisableBulkMonitoringCancelButton(true);
      return;
    }

    setDisableFileDownloadButton(false);

    for (const id of selectedRowIds) {
      const summary = apiResponseData.summaries.find((summary) => {
        return summary.id === id;
      });
      if (summary?.monthlyUpdatable) {
        setDisableBulkMonitoringCancelButton(false);
        return;
      }
    }

    setDisableBulkMonitoringCancelButton(true);
  }, [selectedRowIds.length]);

  if (!realEstateRegistrationMonitoring) return null;

  return (
    <PagePaper>
      <Box sx={{ mb: 3 }}>
        {realEstateRegistrationMonitoringInformation ? (
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography variant="h6">モニタリング結果</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Alert
                severity="info"
                sx={{
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
              >
                <Box
                  style={{
                    maxHeight: "20em",
                    overflow: "auto",
                  }}
                >
                  {isLoading ? (
                    <Stack alignItems="center">
                      <CircularProgress sx={{ my: "20px" }} />
                    </Stack>
                  ) : (
                    <Grid container direction="column" spacing={1}>
                      {notifications.length ? (
                        notifications.map((notification, index) => (
                          <Grid item key={`notification-monitoring-${index}`}>
                            <Typography
                              component={Link}
                              sx={{ cursor: "pointer" }}
                              onClick={() => {
                                handleNotificationLabelClick(notification);
                              }}
                            >
                              {format(
                                new Date(notification.monitoringDate),
                                "yyyy/MM/dd"
                              )}{" "}
                              {notification.name}
                              に異動が発生しました
                            </Typography>
                          </Grid>
                        ))
                      ) : (
                        <Grid item>異動はありません</Grid>
                      )}
                    </Grid>
                  )}
                </Box>
              </Alert>
            </AccordionDetails>
          </Accordion>
        ) : (
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography variant="h6">お知らせ</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ReceptionBookImportStatus importStatuses={importStatuses} />
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
      <form>
        {/* ページタイトル */}
        <Box sx={{ display: "flex", mb: 3 }}>
          <MonitorHeartIcon sx={{ mr: 1 }} fontSize="large" />
          <PageTitle>不動産登記モニタリング</PageTitle>
        </Box>

        <SectionPaper>
          {/* モニタリング概要検索条件入力欄 */}
          <Grid container columnSpacing={2} rowSpacing={1} py={3} px={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                一覧テーブルの絞り込み
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  rowGap: 2,
                }}
              >
                <DateRangePicker
                  startDateLabel="登録日 開始"
                  startDate={createdDateStart}
                  onChangeStartDate={setCreatedDateStart}
                  endDateLabel="登録日 終了"
                  endDate={createdDateEnd}
                  onChangeEndDate={setCreatedDateEnd}
                  minDate={minDate}
                  maxDate={maxDate}
                  defaultCalendarMonth={startDate}
                  displayDateLabel={true}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Label required={false}>登記原因</Label>
                <MonitoringListMultiSelect
                  label="登記原因"
                  value={receptionReasons}
                  onChange={setReceptionReasons}
                  options={receptionReasonOptions}
                  size={SizingWrapperStyle.MEDIUM}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-end" },
                  flexDirection: { xs: "column", sm: "row" },
                  rowGap: 1,
                  columnGap: 2,
                }}
              >
                <LinkButton
                  variant={ButtonVariantOption.Outlined}
                  href="https://docs.google.com/forms/d/e/1FAIpQLSdYazgZcqCbsObM9kcNVpHCgkQjJ2XtYWSx_L-Qw4OTJtCkSA/viewform"
                  sx={{ textAlign: "center", fontWeight: "bold" }}
                  wrapperSize={SizingWrapperStyle.INHERIT}
                >
                  データクレンジング
                  <br />
                  および登録依頼
                </LinkButton>
                <LinkButton
                  variant={ButtonVariantOption.Outlined}
                  href="/monitoring/upload"
                  sx={{ textAlign: "center", fontWeight: "bold" }}
                  wrapperSize={SizingWrapperStyle.INHERIT}
                >
                  モニタリングデータ
                  <br />
                  アップロード
                </LinkButton>
              </Box>
            </Grid>

            <Grid item xs={12} />

            {/* モニタリング概要 一覧 */}
            <Grid item xs={12}>
              <MonitoringFeedGrid
                ref={ref}
                filterCondition={{
                  createdDateStart: getFormattedDate(createdDateStart),
                  createdDateEnd: getFormattedDate(createdDateEnd),
                  receptionReasonId: receptionReasons.map((reason) =>
                    Number(reason)
                  ),
                }}
                setApiResponseData={setApiResponseData}
                setSelectedRowIds={setSelectedRowIds}
                selectedRowIds={selectedRowIds}
              />
            </Grid>

            {/* モニタリング概要 一括操作 */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-start" },
                  flexDirection: { xs: "column", sm: "row" },
                  rowGap: 1,
                  columnGap: 2,
                }}
              >
                <Button
                  label="一括ダウンロード"
                  onClick={handleCsvDownloadButtonClick}
                  variant={ButtonVariantOption.Outlined}
                  size={SizingWrapperStyle.MEDIUM}
                  disabled={disableFileDownloadButton}
                />
                <Button
                  label="モニタリング登録を解除する"
                  onClick={() => {
                    setIsOpen(true);
                  }}
                  variant={ButtonVariantOption.Outlined}
                  size={SizingWrapperStyle.MEDIUM}
                  disabled={disableBulkMonitoringCancelButton}
                  color={ButtonColorOption.Warning}
                />
              </Box>
            </Grid>
          </Grid>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ pt: 2 }}>
          <Link
            component={RouterLink}
            to={mypageTop ? "/mypage" : "/feed"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>
                {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
              </Typography>
            </Box>
          </Link>
        </Box>
      </form>
      {/* モニタリング結果を表示する際に使用するコンポーネント */}
      <MonitoringResultModal
        open={openResultDialog}
        setOpen={setOpenResultDialog}
        apiClient={apiClient}
        monitoringId={monitoringIdForResultDialog}
        fullWidth={true}
        maxWidth={"md"}
      />
      {/* 最終確認モーダル */}
      <CustomModal
        isOpen={isOpen}
        handleClose={(
          event: Record<string, unknown>,
          reason: "backdropClick" | "escapeKeyDown"
        ): void => {
          if (
            reason &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          setIsOpen(false);
        }}
        ariaLabelledby="confirm-modal"
        ariaDescribedby="confirm-modal-description"
      >
        <PageTitle>確認</PageTitle>
        <Box>
          <Typography>
            実行すると元に戻すことはできません。よろしいですか？
          </Typography>

          <Box
            sx={{
              mr: 2,
              my: 3,
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              label="いいえ"
              disabled={isPushedConfirmButton}
              onClick={() => {
                setIsOpen(false);
              }}
              variant={ButtonVariantOption.Outlined}
            />
            <SubmitButton
              label={"はい"}
              timeout={120_000} // ms
              onClick={handleSelectedRowCancel}
              variant={ButtonVariantOption.Contained}
            />
          </Box>
        </Box>
      </CustomModal>
    </PagePaper>
  );
};
